import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { LogService } from '@core/services/log.service';
import { retrieveQuoteSuccess } from '@core/store/retrieve/retrieve.action';
import { updateDwellingReplacementCostForm } from '@core/store/entities/confirmations/confirmations.action';
import {
  CompositeActions,
  CoverageActions,
  EligibilityFormActions,
  QuoteActions,
  ScheduledCategoryActions,
  SessionActions,
  TaskActions,
} from '@core/store/actions';
import { PcUrlService } from '@shared/services/pc-url.service';
import { of, combineLatest } from 'rxjs';
import { EligibilityFormSelectors, ProductsSelectors, TaskSelectors } from '@core/store/selectors';
import { TaskService } from '@core/services/task.service';
import { filterOutNull } from '@shared/rxjs/filter-out-null.operator';
import { StringUtils } from '@shared/utils/string.utils';
import * as PropertyFormSelectors from '@core/store/property-form/property-form.selector';
import {
  rateBindSuccess,
  rateQuoteSuccess,
  updateQuoteSuccess,
} from '@entities/quote/quote.action';
import { ProductsService } from '@core/services/products.service';
import { QuoteService } from '@core/services/quote.service';
import { DsmSelectors } from '@core/store/selectors';
import { PropertyProductTypes } from '@shared/constants/app-constants';
import { EligibilityFormModel } from '@app/eligibility/store/eligibility-form.model';
import { removeProduct } from '@entities/product/product.actions';

@Injectable({
  providedIn: 'root',
})
export class TaskEffects {
  constructor(
    private store: Store,
    private actions$: Actions,
    private taskService: TaskService,
    private loggingService: LogService,
    private pcUrlService: PcUrlService,
    private productsService: ProductsService,
    private quoteService: QuoteService,
    private window: Window
  ) { }

  rebuildTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.rebuildTasks),
      switchMap(() => [
        TaskActions.clearTasksExceptCompletedReview(),
        TaskActions.updateTasks(),
      ])
    )
  );

  logTasksOnChange$ = createEffect(
    () =>
      this.store.select(TaskSelectors.getIncompleteTasksLogMessage).pipe(
        distinctUntilChanged(),
        tap((message) =>
          this.loggingService.logUiEvent('tasks', message, 'task')
        )
      ),
    { dispatch: false }
  );

  rebuildTasksWhenSelectedProductsChange$ = createEffect(() =>
    this.store.select(ProductsSelectors.getSelectedProductTypes).pipe(
      distinctUntilChanged((prev, next) =>
        StringUtils.unorderedStringListsEquivalent(prev, next)
      ),
      map(() => TaskActions.rebuildTasks())
    )
  );

  rebuildTasksAfterInitiation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompositeActions.submitPniPageSuccess),
      switchMap(() => [
        TaskActions.clearTasksExceptCompletedReview(),
        TaskActions.updateTasks(),
      ])
    )
  );

  updateCoverageTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CoverageActions.getAllCoveragesSuccess,
        CoverageActions.replaceCoveragesForProduct,
        ScheduledCategoryActions.updateScheduledCategorySuccess,
        updateDwellingReplacementCostForm,
        retrieveQuoteSuccess,
        TaskActions.updateTasks
      ),
      switchMap(() =>
        this.store.select(TaskSelectors.updateCoverageTasks).pipe(take(1))
      ),
      map((tasks) => this.taskService.createUpdateActions(tasks)),
      switchMap((actions) => [...actions])
    )
  );

  updateAccountTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rateQuoteSuccess),
      withLatestFrom(this.productsService.getOverallQuoteStatus()),
      filter(([_, status]) => status === 'Quoted' || status === 'Binding'),
      switchMap(() =>
        this.store.select(TaskSelectors.updateAccountTasks).pipe(take(1))
      ),
      map((tasks) => this.taskService.createUpdateActions(tasks)),
      switchMap((actions) => [...actions])
    )
  );

  updateAccountTasksAfterRegistrationModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompositeActions.submitAccountRegistrationModalSuccess),
      switchMap(() =>
        this.store.select(TaskSelectors.updateAccountTasks).pipe(take(1))
      ),
      map((tasks) => this.taskService.createUpdateActions(tasks)),
      switchMap((actions) => [...actions])
    )
  );

  updateReviewTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(retrieveQuoteSuccess, TaskActions.updateTasks),
      switchMap(() =>
        this.store.select(TaskSelectors.updateReviewTasks).pipe(take(1))
      ),
      map((tasks) => this.taskService.createUpdateActions(tasks)),
      switchMap((actions) => [...actions])
    )
  );

  // When a quote is retrieved and registration info already contains consent or a mobile number,
  completeRegistrationReviewOnRetrieve$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.updateTasks),
      withLatestFrom(
        this.store.select(
          ProductsSelectors.getNationwideAccountRegistrationInfo
        ),
        this.store.select(TaskSelectors.getReviewTasks)
      ),
      map(([action, registrationInfo, reviewTasks]) => {
        const alreadyRegistered =
          !!registrationInfo?.hasConsentedToOneTimeRegistrationText ||
          !!registrationInfo?.primaryNamedInsuredMobileNumber;

        if (!alreadyRegistered) return [];

        const targetTasks = reviewTasks.filter(
          (t) => t.field === 'onlineAccountRegistrationReview' && !t.completed
        );

        return targetTasks.map((task) =>
          TaskActions.completeTask({ payload: task })
        );
      }),
      filter((actions) => actions.length > 0),
      switchMap((actions) => actions)
    )
  );

  updateTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuoteActions.rateQuoteSuccess, QuoteActions.rateBindSuccess),
      map(() => TaskActions.updateTasks())
    )
  );

  updateTasksWhenRoofYearBecomesImmutable$ = createEffect(() =>
    this.store.select(PropertyFormSelectors.getAllRoofYearImmutable).pipe(
      distinctUntilChanged(
        (prev, next) => JSON.stringify(prev) === JSON.stringify(next)
      ),
      filter((obj) => Object.values(obj).some((v) => v)),
      map(() => TaskActions.rebuildTasks())
    )
  );

  openInPolicyCenter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.openInPolicyCenter),
      switchMap((action) =>
        this.pcUrlService.getPolicyCenterUrl(action.quoteId, 'Job').pipe(
          take(1),
          map((url) => ({ url, action }))
        )
      ),
      switchMap(({ url, action }) => {
        this.loggingService.logBusinessEvent('pivot-to-pc', action.reason);
        this.window.open(url);
        return of(TaskActions.openInPolicyCenterSuccess());
      }),
      catchError((error) => of(TaskActions.openInPolicyCenterFail()))
    )
  );

  aliasDismissReviewTaskAsCompleteTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.dismissReviewTask),
      withLatestFrom(this.store.select(TaskSelectors.getReviewTasks)),
      map(([action, tasks]) =>
        tasks.find((t) => t.field === action.payload.field)
      ),
      filterOutNull(),
      map((task) => TaskActions.completeTask({ payload: task }))
    )
  );

  rateDraftProductsOnSubmitTasksModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TaskActions.submitTasksModal),
        switchMap(() =>
          combineLatest([
            this.productsService.getSelectedProducts(),
            this.store.select(DsmSelectors.getDsmCallNames),
          ]).pipe(take(1))
        ),
        map(
          ([selectedProducts, callsInFlight]) =>
            selectedProducts?.filter(
              (product) => !callsInFlight.includes(`${product.type}-quote`)
            ) || []
        ),
        tap((filteredProducts) =>
          this.quoteService.rateProductsSpinner(filteredProducts)
        )
      ),
    { dispatch: false }
  );

  detectDwellingFireOrphaned$ = createEffect(() =>
    this.store.select(ProductsSelectors.getSelectedProductTypes).pipe(
      pairwise(),
      filter(([prevTypes, currentTypes]) => {
        const prevHasProperty = prevTypes.some((type) =>
          PropertyProductTypes.includes(type)
        );
        const prevHasDwellingFire = prevTypes.includes('DwellingFire');
        const currentHasProperty = currentTypes.some((type) =>
          PropertyProductTypes.includes(type)
        );
        const currentHasDwellingFire = currentTypes.includes('DwellingFire');

        return (
          prevHasProperty &&
          prevHasDwellingFire &&
          !currentHasProperty &&
          currentHasDwellingFire
        );
      }),
      switchMap(() =>
        this.actions$.pipe(
          ofType(TaskActions.updateTasks),
          take(1),
          map(() =>
            TaskActions.addTask({
              payload: {
                entityType: 'dwellingFireProduct',
                productType: 'DwellingFire',
                field: 'dwellingFireOrphaned',
                ratingStatusOrdinal: 'Draft',
                message:
                  'Dwelling Fire requires the primary residence to be insured with Nationwide.',
                overrideTitle: 'Dwelling Fire',
                overrideAction: removeProduct({ payload: 'DwellingFire' }),
                overrideActionMessage: 'Remove Dwelling Fire',
              },
            })
          )
        )
      )
    )
  );
}
